import { readFile, stat } from "node:fs/promises";
import { builtinModules } from "node:module";
import path from "node:path";

const INTERNAL_EXTENSIONS = [
  ".ts",
  ".tsx",
  ".js",
  ".jsx",
  ".mjs",
  ".cjs",
  ".json",
];

const PARSEABLE_EXTENSIONS = new Set([
  ".ts",
  ".tsx",
  ".js",
  ".jsx",
  ".mjs",
  ".cjs",
]);

const BUILTIN_MODULES = new Set([
  ...builtinModules,
  ...builtinModules.map((item) => `node:${item}`),
]);

const STATIC_IMPORT_RE =
  /\bimport\s+(?:type\s+)?(?:[\s\S]*?\sfrom\s+)?["']([^"']+)["']/g;
const EXPORT_FROM_RE = /\bexport\s+(?:[\s\S]*?\sfrom\s+)["']([^"']+)["']/g;
const DYNAMIC_IMPORT_RE = /\bimport\s*\(\s*["']([^"']+)["']\s*\)/g;
const REQUIRE_RE = /\brequire\s*\(\s*["']([^"']+)["']\s*\)/g;

export type ImportGraphResult = {
  internalFiles: string[];
  externalPackages: string[];
};

type BuildImportGraphInput = {
  projectRoot: string;
  entryFiles: string[];
  followInternalImports?: boolean;
};

type ResolvedImport =
  | {
      kind: "internal";
      filePath: string;
    }
  | {
      kind: "external";
      packageName: string;
    }
  | {
      kind: "unknown";
    };

function collectSpecifiers(content: string): string[] {
  const results = new Set<string>();
  let match: RegExpExecArray | null = null;

  for (const re of [
    STATIC_IMPORT_RE,
    EXPORT_FROM_RE,
    DYNAMIC_IMPORT_RE,
    REQUIRE_RE,
  ]) {
    re.lastIndex = 0;
    while ((match = re.exec(content)) !== null) {
      const specifier = match[1]?.trim();
      if (specifier) {
        results.add(specifier);
      }
    }
  }

  return [...results];
}

function getExternalPackageName(specifier: string): string | null {
  if (!specifier || specifier.startsWith(".") || specifier.startsWith("/")) {
    return null;
  }

  if (specifier.startsWith("node:")) {
    return null;
  }

  if (specifier.startsWith("@/") || specifier.startsWith("@root/")) {
    return null;
  }

  const parts = specifier.split("/");
  if (parts.length === 0) {
    return null;
  }

  if (specifier.startsWith("@")) {
    if (parts.length < 2) {
      return null;
    }
    return `${parts[0]}/${parts[1]}`;
  }

  return parts[0] ?? null;
}

async function pathExists(filePath: string): Promise<boolean> {
  try {
    await stat(filePath);
    return true;
  } catch {
    return false;
  }
}

async function resolveFileCandidate(
  candidateBase: string,
): Promise<string | null> {
  const ext = path.extname(candidateBase);

  if (ext) {
    if (await pathExists(candidateBase)) {
      return candidateBase;
    }
    return null;
  }

  for (const extension of INTERNAL_EXTENSIONS) {
    const withExtension = `${candidateBase}${extension}`;
    if (await pathExists(withExtension)) {
      return withExtension;
    }
  }

  if (await pathExists(candidateBase)) {
    const stats = await stat(candidateBase);
    if (stats.isFile()) {
      return candidateBase;
    }

    if (stats.isDirectory()) {
      for (const extension of INTERNAL_EXTENSIONS) {
        const indexCandidate = path.join(candidateBase, `index${extension}`);
        if (await pathExists(indexCandidate)) {
          return indexCandidate;
        }
      }
    }
  }

  return null;
}

async function resolveImportToFile(
  projectRoot: string,
  importerFilePath: string,
  specifier: string,
): Promise<ResolvedImport> {
  const externalPackageName = getExternalPackageName(specifier);
  if (externalPackageName) {
    if (!BUILTIN_MODULES.has(externalPackageName)) {
      return {
        kind: "external",
        packageName: externalPackageName,
      };
    }
    return { kind: "unknown" };
  }

  let candidateBase: string | null = null;
  if (specifier.startsWith("@/")) {
    candidateBase = path.join(projectRoot, "src", specifier.slice(2));
  } else if (specifier.startsWith("@root/")) {
    candidateBase = path.join(projectRoot, specifier.slice(6));
  } else if (specifier.startsWith(".")) {
    candidateBase = path.resolve(path.dirname(importerFilePath), specifier);
  } else if (specifier.startsWith("/")) {
    candidateBase = path.join(projectRoot, specifier.slice(1));
  } else {
    return { kind: "unknown" };
  }

  const resolved = await resolveFileCandidate(candidateBase);
  if (!resolved) {
    return { kind: "unknown" };
  }

  return {
    kind: "internal",
    filePath: resolved,
  };
}

function shouldParseFile(filePath: string): boolean {
  return PARSEABLE_EXTENSIONS.has(path.extname(filePath).toLowerCase());
}

export async function buildImportGraph(
  input: BuildImportGraphInput,
): Promise<ImportGraphResult> {
  const shouldFollowInternalImports = input.followInternalImports ?? true;
  const projectRoot = path.resolve(input.projectRoot);
  const queue = input.entryFiles.map((filePath) => path.resolve(filePath));
  const visited = new Set<string>();
  const internalFiles = new Set<string>();
  const externalPackages = new Set<string>();

  while (queue.length > 0) {
    const currentFilePath = queue.shift();
    if (!currentFilePath || visited.has(currentFilePath)) {
      continue;
    }

    visited.add(currentFilePath);
    internalFiles.add(currentFilePath);

    if (!shouldParseFile(currentFilePath)) {
      continue;
    }

    let content = "";
    try {
      content = await readFile(currentFilePath, "utf-8");
    } catch {
      continue;
    }

    const specifiers = collectSpecifiers(content);
    for (const specifier of specifiers) {
      const resolved = await resolveImportToFile(
        projectRoot,
        currentFilePath,
        specifier,
      );

      if (resolved.kind === "internal") {
        if (shouldFollowInternalImports && !visited.has(resolved.filePath)) {
          queue.push(resolved.filePath);
        }
        continue;
      }

      if (resolved.kind === "external") {
        externalPackages.add(resolved.packageName);
      }
    }
  }

  return {
    internalFiles: [...internalFiles].sort(),
    externalPackages: [...externalPackages].sort(),
  };
}
